<template>
  <section class="pitching-section" :aria-label="`${teamName} Projected Pitching`">
    <div class="pitching-section__inner">
      <div class="section__header">
        <div class="section-number">{{ sectionNumber }}</div>
        <div class="section__titles">
          <p class="eyebrow">Projected Pitching Line &mdash; {{ teamLabel }}</p>
          <h2 class="display-lg">{{ teamName }} <em>staff.</em></h2>
        </div>
      </div>

      <hr class="rule rule--double" />

      <article class="paper-card pitching-card">
        <div class="card__scroll">
          <table class="pitching-table">
            <thead>
              <tr>
                <th scope="col" class="th--player">Pitcher</th>
                <th scope="col" class="th--num">IP</th>
                <th scope="col" class="th--num">K</th>
                <th scope="col" class="th--num">BB</th>
                <th scope="col" class="th--num">H</th>
                <th scope="col" class="th--num">ER</th>
                <th scope="col" class="th--num">HR</th>
                <th scope="col" class="th--chevron" aria-hidden="true"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="pitcher in pitchers"
                :key="pitcher.player_id"
                class="pitching-row"
                :class="{
                  'pitching-row--active': activePitcherId === pitcher.player_id,
                  'row--starter': pitcher.is_starter
                }"
                @click="toggle(pitcher)"
              >
                <td class="td--player">
                  <span v-if="pitcher.is_starter" class="starter-eyebrow">Starter</span>
                  <span class="player-name">{{ pitcher.name }}</span>
                  <span v-if="pitcher.is_starter" class="badge-sp">SP</span>
                  <span v-if="edgesFor(pitcher.player_id).length" class="prop-chips" @click.stop>
                    <span
                      v-for="pe in edgesFor(pitcher.player_id)"
                      :key="`${pe.market}-${pe.line}`"
                      class="prop-chip"
                      :class="edgeClass(pe.edge)"
                      :title="`${propMarketLabel(pe.market)} ${pe.line}: sim ${formatPct(
                        pe.sim_over_pct
                      )} vs implied ${formatPct(pe.implied_over_pct)}`"
                    >
                      {{ propMarketShort(pe.market) }} {{ pe.line }}
                      {{ formatEdge(pe.edge) }}
                    </span>
                  </span>
                </td>
                <td class="td--num numeric">{{ pitcher.mean_ip.toFixed(1) }}</td>
                <td class="td--num numeric">{{ pitcher.mean_k.toFixed(1) }}</td>
                <td class="td--num numeric">{{ pitcher.mean_bb.toFixed(1) }}</td>
                <td class="td--num numeric">{{ pitcher.mean_h.toFixed(1) }}</td>
                <td class="td--num numeric">{{ pitcher.mean_er.toFixed(1) }}</td>
                <td class="td--num numeric">{{ pitcher.mean_hr.toFixed(1) }}</td>
                <td class="td--chevron" aria-hidden="true">
                  <span
                    class="chevron"
                    :class="{ 'chevron--open': activePitcherId === pitcher.player_id }"
                    >›</span
                  >
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </article>
    </div>
  </section>
</template>

<script setup lang="ts">
import type { PitcherProjectionResponse, PropEdge } from '../../api/games'
import {
  edgeClass,
  formatEdge,
  formatPct,
  propMarketLabel,
  propMarketShort
} from '../../utils/odds'

const props = defineProps<{
  pitchers: PitcherProjectionResponse[]
  teamName: string
  teamLabel: string
  sectionNumber: string
  activePitcherId: number | null
  propEdges?: PropEdge[]
}>()

const emit = defineEmits<{
  select: [pitcher: PitcherProjectionResponse | null]
}>()

function toggle(pitcher: PitcherProjectionResponse) {
  emit('select', props.activePitcherId === pitcher.player_id ? null : pitcher)
}

function edgesFor(playerId: number): PropEdge[] {
  return (props.propEdges ?? []).filter(p => p.player_id === playerId)
}
</script>

<style>
@import '../../assets/projected-table.css';

/* Pitching-specific overrides on top of shared projected-table.css. */
.pitching-table {
  min-width: 480px;
}

.pitching-table .row--starter td {
  border-top: var(--rule-med) solid var(--ink);
}

.pitching-table .starter-eyebrow {
  display: block;
  font-family: var(--font-ui);
  font-size: 0.55rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.18em;
  color: var(--ox);
  margin-bottom: 0.1rem;
}

.pitching-table .badge-sp {
  display: inline-block;
  margin-left: 0.4rem;
  font-family: var(--font-ui);
  font-size: 0.55rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: var(--paper);
  background: var(--ox);
  padding: 0.1rem 0.3rem;
  border-radius: 2px;
  vertical-align: middle;
}
</style>
